import React, { useContext, useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Portal from "@material-ui/core/Portal";
import { useTheme, useMediaQuery, makeStyles } from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import ColorModeContext from "../../layout/themeContext";
import { createGradientStyles } from "../../utils/themeColors";

const useStyles = makeStyles((theme) => ({
	dialogTitle: {
		padding: theme.spacing(2, 3),
		'& .MuiTypography-root': {
			fontWeight: 600,
			fontSize: '1.2rem'
		}
	},
	...createGradientStyles(theme),
	dialogContent: {
		padding: theme.spacing(3),
		backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#fafafa'
	},
	dialogActions: {
		padding: theme.spacing(2, 3),
		backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#f5f5f5',
		borderTop: theme.palette.type === 'dark' ? '1px solid #444' : '1px solid #e0e0e0'
	}
}));

const ConfirmationModal = ({ title, children, open, onClose, onConfirm, confirmationText, confirmationLabel }) => {
	const classes = useStyles();
	const theme = useTheme();
	const colorModeContext = useContext(ColorModeContext);
	const useGradient = colorModeContext?.useGradient || theme.palette?.gradient || false;
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const [typedConfirmation, setTypedConfirmation] = useState("");
	const requiresTextConfirmation = !!confirmationText;
	const confirmationMatches = !requiresTextConfirmation ||
		typedConfirmation.trim().toUpperCase() === String(confirmationText).trim().toUpperCase();

	useEffect(() => {
		if (!open) {
			setTypedConfirmation("");
		}
	}, [open]);

	const handleConfirm = () => {
		if (!confirmationMatches) return;
		console.log('[ConfirmationModal] Confirmando exclusão');
		onClose(false);
		onConfirm();
	};

	const handleCancel = () => {
		console.log('[ConfirmationModal] Cancelando exclusão');
		onClose(false);
	};

	return (
		<Portal container={document.body}>
			<Dialog
				open={open}
				onClose={(event, reason) => {
					console.log('[ConfirmationModal] Dialog onClose, reason:', reason);
					if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
						handleCancel();
					}
				}}
				aria-labelledby="confirm-dialog"
				disableEnforceFocus={true}
				disableAutoFocus={true}
				disableRestoreFocus={true}
				fullWidth={isMobile}
				maxWidth={isMobile ? false : "sm"}
				PaperProps={{
					style: {
						zIndex: 10003,
						position: 'fixed',
						margin: isMobile ? '16px' : '24px',
						maxWidth: isMobile ? 'calc(100% - 32px)' : '400px',
						width: isMobile ? 'calc(100% - 32px)' : 'auto'
					}
				}}
				BackdropProps={{
					style: {
						zIndex: 10002,
						backgroundColor: 'rgba(0, 0, 0, 0.5)',
						position: 'fixed'
					}
				}}
				style={{
					zIndex: 10003,
					position: 'fixed'
				}}
			>
				<DialogTitle 
					id="confirm-dialog" 
					className={`${classes.dialogTitle} ${useGradient ? classes.gradientBackground : classes.solidBackground}`}
					style={{ padding: isMobile ? '16px' : '24px' }}
				>
					{title}
				</DialogTitle>
				<DialogContent dividers className={classes.dialogContent} style={{ padding: isMobile ? '16px' : '24px' }}>
					<Typography>{children}</Typography>
					{requiresTextConfirmation ? (
						<TextField
							fullWidth
							variant="outlined"
							margin="normal"
							value={typedConfirmation}
							onChange={(event) => setTypedConfirmation(event.target.value)}
							label={confirmationLabel || `Digite ${confirmationText}`}
						/>
					) : null}
				</DialogContent>
				<DialogActions className={classes.dialogActions} style={{ padding: isMobile ? '8px 16px' : '16px 24px' }}>
					<Button
						variant="contained"
						onClick={handleCancel}
						color="default"
						style={{
							pointerEvents: 'auto',
							touchAction: 'manipulation',
							minWidth: '80px',
							minHeight: '36px'
						}}
					>
						{i18n.t("confirmationModal.buttons.cancel")}
					</Button>
					<Button
						variant="contained"
						onClick={handleConfirm}
						color="secondary"
						disabled={!confirmationMatches}
						style={{
							pointerEvents: 'auto',
							touchAction: 'manipulation',
							minWidth: '80px',
							minHeight: '36px'
						}}
					>
						{i18n.t("confirmationModal.buttons.confirm")}
					</Button>
				</DialogActions>
			</Dialog>
		</Portal>
	);
};

export default ConfirmationModal;
